import { PaginatedResponse, PaginationParams } from '@console/utils';
import { apiClient } from './client';

export interface MonthlyConsumption {
  id: string;
  contractId: string;
  month: number;
  year: number;
  hoursLimit: number;
  consumedHours: number;
  contract?: {
    id: string;
    number: string;
    company: {
      id: string;
      name: string;
    };
  };
  tickets?: TicketSummary[];
}

export interface MonthlyConsumptionFilters extends PaginationParams {
  contractId?: string;
  companyId?: string;
}

export interface TicketSummary {
  id: string;
  number: string;
  title: string;
  date: string;
  debitedTime: number;
  user: {
    name: string;
    email: string;
  };
}

export interface TeamHoursStatistic {
  id: string;
  name: string;
  totalHours: number;
  ticketCount: number;
}

export const monthlyConsumptionApi = {
  getAll: async (
    params?: MonthlyConsumptionFilters
  ): Promise<PaginatedResponse<MonthlyConsumption>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.contractId) searchParams.append('contractId', params.contractId);
    if (params?.companyId) searchParams.append('companyId', params.companyId);

    const queryString = searchParams.toString();
    const response = await apiClient.get(
      `/monthly-consumption${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },

  getByContract: async (contractId: string): Promise<MonthlyConsumption[]> => {
    const response = await apiClient.get(`/monthly-consumption/contract/${contractId}`);
    return response.data;
  },

  getAllMonthlyConsumptions: async (): Promise<MonthlyConsumption[]> => {
    // Get all contracts first, then fetch monthly consumptions for each
    const contractsResponse = await apiClient.get('/contracts');
    const contracts = contractsResponse.data.data || [];

    const allConsumptions: MonthlyConsumption[] = [];

    for (const contract of contracts) {
      try {
        const consumptions = await monthlyConsumptionApi.getByContract(contract.id);
        // Add contract information to each consumption
        const consumptionsWithContract = consumptions.map((consumption) => ({
          ...consumption,
          contract: {
            id: contract.id,
            number: contract.number,
            company: contract.company,
          },
        }));
        allConsumptions.push(...consumptionsWithContract);
      } catch {
        console.warn(`Failed to fetch consumptions for contract ${contract.id}`);
      }
    }

    return allConsumptions.sort((a, b) => {
      // Sort by year desc, then month desc
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  },

  getSpecific: async (
    contractId: string,
    year: number,
    month: number
  ): Promise<MonthlyConsumption> => {
    const response = await apiClient.get(
      `/monthly-consumption/contract/${contractId}/${year}/${month}`
    );
    return response.data;
  },

  recalculate: async (contractId: string, year: number, month: number): Promise<void> => {
    await apiClient.post(
      `/monthly-consumption/contract/${contractId}/${year}/${month}/recalculate`
    );
  },

  getTeamHoursStatistics: async (filters?: {
    contractId?: string;
    companyId?: string;
    month?: number;
    year?: number;
  }): Promise<TeamHoursStatistic[]> => {
    const searchParams = new URLSearchParams();
    if (filters?.contractId) searchParams.append('contractId', filters.contractId);
    if (filters?.companyId) searchParams.append('companyId', filters.companyId);
    if (filters?.month) searchParams.append('month', filters.month.toString());
    if (filters?.year) searchParams.append('year', filters.year.toString());

    const queryString = searchParams.toString();
    const response = await apiClient.get(
      `/monthly-consumption/team-hours${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },
};
